const m = require('minthril');
const { component } = require('../../../component');
const { Breadcrumbs } = require('../../../components/breadcrumbs');

const AstroComparisonDocsPage = component(() => ({
  view: () => m('article.panel.docs-page.astro-comparison', [
    m(Breadcrumbs, {
      items: [
        { label: 'Docs', href: '/docs' },
        { label: 'Comparisons', href: '/docs/comparisons' },
        { label: 'Astro: Mid Engine Co' }
      ]
    }),
    m('h2', 'Building Mid Engine Co with Astro'),
    m('p',
      'Mid Engine Co is a custom storefront backed by Shopify. Its browser app loads public product data, ' +
      'manages each visitor’s cart, and sends buyers to Shopify checkout. Here is what an Astro version ' +
      'could look like—and the additional rendering decisions that would come with it.'
    ),
    m('h3', 'A likely Astro architecture'),
    m('ol', [
      m('li', [
        m('strong', 'Render product and collection pages as HTML. '),
        'Astro can fetch product data from Shopify while building the site, or fetch it on the server for each request.'
      ]),
      m('li', [
        m('strong', 'Make the cart an interactive browser island. '),
        'The cart belongs to the visitor, so browser code would create or retrieve that visitor’s Shopify cart and update it as they shop.'
      ]),
      m('li', [
        m('strong', 'Hand checkout to Shopify. '),
        'The storefront sends the buyer to the checkout URL returned for their cart.'
      ])
    ]),
    m('p', [
      'Astro supports both pre-rendered pages and on-demand server rendering. Its islands architecture lets ' +
      'the product page arrive as HTML while only interactive pieces, such as the cart, load browser JavaScript. ',
      m('a', { href: 'https://docs.astro.build/en/guides/on-demand-rendering/' }, 'Astro rendering modes'),
      ' · ',
      m('a', { href: 'https://docs.astro.build/en/concepts/islands/' }, 'Astro islands')
    ]),
    m('h3', 'Decisions the Astro implementation needs'),
    m('ul', [
      m('li', [
        m('strong', 'When product data becomes HTML. '),
        'Build-time generation is quick to serve, but a product change can require rebuilding pages. Request-time rendering keeps data fresher, but adds a server runtime and makes page delivery depend on the Shopify API unless caching is designed around it.'
      ]),
      m('li', [
        m('strong', 'How product changes refresh generated pages. '),
        'For a static build, Shopify updates need to trigger a rebuild or a targeted regeneration mechanism. Otherwise page copy, pricing, or availability can lag behind the catalog.'
      ]),
      m('li', [
        m('strong', 'How dynamic product routes are generated. '),
        'The build needs to discover product and collection handles, handle pagination and variants, and decide what happens when a product is added or removed.'
      ]),
      m('li', [
        m('strong', 'Where the browser/server boundary sits. '),
        'The cart must use the real visitor’s cart identity. If any cart or account data is fetched server-side, responses must not leak between visitors through shared page or CDN caches.'
      ]),
      m('li', [
        m('strong', 'Which pieces hydrate and when. '),
        'Astro pages are HTML by default; interactive cart controls need client JavaScript and a hydration strategy. The team must wire the island to the existing cart behavior and state.'
      ]),
      m('li', [
        m('strong', 'How the Shopify integration and deployment fit together. '),
        'A server-rendered setup needs an Astro adapter and deployment runtime. Build-time Shopify credentials and server-only secrets also need to stay out of browser bundles.'
      ])
    ]),
    m('h3', 'What changes with Spache'),
    m('p',
      'With Spache, Mid Engine Co keeps its SPA and its existing browser-side Shopify integration. Spache prepares ' +
      'the anonymous public product page and serves that rendered HTML; then the visitor’s app starts in their ' +
      'browser and loads their own cart. The cart is not part of the shared render.'
    ),
    m('p',
      'That means there is no Astro adapter to deploy, no Astro product-page rendering mode to choose, and no ' +
      'Astro island to build for the cart. Spache still has cache freshness and invalidation settings, and public ' +
      'product data must be kept current—but the existing app does not need to be restructured into build-time ' +
      'pages, server-rendered pages, and hydrated islands just to provide rendered HTML.'
    ),
    m('h3', 'Technical considerations'),
    m('p',
      'For each concern, Astro provides a way to implement it; the team still chooses and connects the pieces. ' +
      'Spache’s “Automatic” means the existing SPA flow handles it without implementing an Astro rendering layer. ' +
      'JSON GET responses are captured automatically; capture rules are only needed for non-GET requests. ' +
      'Catalog freshness remains configurable.'
    ),
    m('div.astro-comparison__table-wrap', [
      m('table.astro-comparison__table', [
        m('thead', m('tr', [
          m('th', 'Technical consideration'),
          m('th', 'Astro implementation'),
          m('th', 'Spache implementation')
        ])),
        m('tbody', [
          m('tr', [
            m('th', 'Product data in crawlable HTML'),
            m('td', 'Fetch Shopify catalog data at build time or during server rendering, then render it into the product page.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Product and collection routes'),
            m('td', 'Map Shopify handles to Astro routes; for static output, enumerate products and collections during the build.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Catalog freshness'),
            m('td', 'Trigger rebuilds or configure request-time rendering and response caching when products change.'),
            m('td', 'Automatic, configurable')
          ]),
          m('tr', [
            m('th', 'Visitor-specific cart'),
            m('td', 'Build a client island that loads and updates the current visitor’s Shopify cart.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Keep personal data out of shared HTML'),
            m('td', 'Keep cart/account requests in the browser, or carefully isolate and disable shared caching for personalized server responses.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Interactive app behavior'),
            m('td', 'Choose Astro client islands and hydration timing for cart controls and other interactive components.'),
            m('td', 'Automatic')
          ]),
          m('tr', [
            m('th', 'Rendering deployment'),
            m('td', 'Choose static build hosting, or add an Astro adapter and server runtime for on-demand rendering.'),
            m('td', 'Automatic')
          ])
        ])
      ])
    ]),
    m('p',
      'Astro is a strong choice if the goal is to build a new HTML-first storefront and deliberately minimize ' +
      'browser JavaScript. Spache is a different trade: keep the working SPA and add a ready-to-serve public ' +
      'render, without making the application adopt a new rendering architecture.'
    )
  ])
}));

module.exports = { AstroComparisonDocsPage };
